/**
 * Mobile Filters Controller
 * Wires up the bottom sheet for filter/sort controls on mobile.
 * Syncs state between bottom sheet UI and libraryView.
 */

import { createBottomSheet } from "./bottomSheet.js";

export function createMobileFilters({ document, window }) {
  let bottomSheet = null;
  let isInitialized = false;

  // State mirrors
  let currentFilter = "all";
  let currentSearchMode = "files";
  let currentSortBy = "created_at";
  let currentSortOrder = "desc";

  // Elements
  let filtersBtn = null;
  let sheetSearchModes = null;
  let sheetFilterOptions = null;
  let sheetSortOptions = null;
  let sheetSortOrder = null;

  function init() {
    if (isInitialized) return;

    // Mobile filters button
    filtersBtn = document.getElementById("mobileFiltersBtn");
    if (!filtersBtn) {
      console.warn("[mobileFilters] Button not found");
      return;
    }

    // Create bottom sheet instance
    bottomSheet = createBottomSheet({
      document,
      window,
      onClose: syncFiltersButtonBadge,
    });

    const initialized = bottomSheet.init(
      "filtersSheet",
      "filtersSheetBackdrop",
    );
    if (!initialized) return;

    // Cache sheet elements
    sheetSearchModes = document.getElementById("sheetSearchModes");
    sheetFilterOptions = document.getElementById("sheetFilterOptions");
    sheetSortOptions = document.getElementById("sheetSortOptions");
    sheetSortOrder = document.getElementById("sheetSortOrder");

    // Wire up filters button
    filtersBtn.addEventListener("click", handleFiltersClick);

    // Wire up done button
    const doneBtn = document.getElementById("filtersSheetDone");
    if (doneBtn) {
      doneBtn.addEventListener("click", () => bottomSheet.close());
    }

    // Wire up search mode chips
    if (sheetSearchModes) {
      sheetSearchModes.addEventListener("click", handleSearchModeClick);
    }

    // Wire up filter options
    if (sheetFilterOptions) {
      sheetFilterOptions.addEventListener("click", handleFilterClick);
    }

    // Wire up sort options
    if (sheetSortOptions) {
      sheetSortOptions.addEventListener("click", handleSortClick);
    }

    // Wire up sort order
    if (sheetSortOrder) {
      sheetSortOrder.addEventListener("click", handleSortOrderClick);
    }

    isInitialized = true;
  }

  function handleFiltersClick() {
    if (bottomSheet) {
      bottomSheet.open();
    }
  }

  function handleSearchModeClick(e) {
    const chip = e.target.closest("[data-mode]");
    if (!chip) return;

    const mode = chip.dataset.mode;
    if (mode === currentSearchMode) return;

    currentSearchMode = mode;

    // Update UI
    const chips = sheetSearchModes.querySelectorAll("[data-mode]");
    chips.forEach((c) =>
      c.classList.toggle("bottom-sheet__chip--active", c.dataset.mode === mode),
    );

    // Call global function
    if (typeof window.setSearchMode === "function") {
      window.setSearchMode(mode);
    }
  }

  function handleFilterClick(e) {
    const option = e.target.closest("[data-filter]");
    if (!option) return;

    const filter = option.dataset.filter;
    if (filter === currentFilter) return;

    currentFilter = filter;

    // Update UI
    const options = sheetFilterOptions.querySelectorAll("[data-filter]");
    options.forEach((o) =>
      o.classList.toggle(
        "bottom-sheet__option--active",
        o.dataset.filter === filter,
      ),
    );

    // Call global function
    if (typeof window.setFilter === "function") {
      window.setFilter(filter);
    }

    syncFiltersButtonBadge();
  }

  function handleSortClick(e) {
    const option = e.target.closest("[data-sort]");
    if (!option) return;

    const sort = option.dataset.sort;
    if (sort === currentSortBy) return;

    currentSortBy = sort;

    // Update UI
    const options = sheetSortOptions.querySelectorAll("[data-sort]");
    options.forEach((o) =>
      o.classList.toggle(
        "bottom-sheet__option--active",
        o.dataset.sort === sort,
      ),
    );

    // Update desktop select and trigger sort
    const desktopSelect = document.getElementById("sort-by");
    if (desktopSelect) {
      desktopSelect.value = sort;
    }
    if (typeof window.sortFiles === "function") {
      window.sortFiles();
    }
  }

  function handleSortOrderClick(e) {
    const chip = e.target.closest("[data-order]");
    if (!chip) return;

    const order = chip.dataset.order;
    if (order === currentSortOrder) return;

    currentSortOrder = order;

    // Update UI
    const chips = sheetSortOrder.querySelectorAll("[data-order]");
    chips.forEach((c) =>
      c.classList.toggle(
        "bottom-sheet__chip--active",
        c.dataset.order === order,
      ),
    );

    // Update desktop select and trigger sort
    const desktopSelect = document.getElementById("sort-order");
    if (desktopSelect) {
      desktopSelect.value = order;
    }
    if (typeof window.sortFiles === "function") {
      window.sortFiles();
    }
  }

  function syncFiltersButtonBadge() {
    if (!filtersBtn) return;

    // Show badge if any non-default filter is active
    const hasActiveFilters =
      currentFilter !== "all" ||
      currentSearchMode !== "files" ||
      currentSortBy !== "created_at" ||
      currentSortOrder !== "desc";

    filtersBtn.classList.toggle("mobile-filters-btn--active", hasActiveFilters);

    // Update badge count
    let count = 0;
    if (currentFilter !== "all") count++;
    if (currentSearchMode !== "files") count++;
    if (currentSortBy !== "created_at") count++;
    if (currentSortOrder !== "desc") count++;

    const badge = filtersBtn.querySelector(".mobile-filters-btn__badge");
    if (badge) {
      badge.textContent = String(count);
    }
  }

  function syncFromDesktop() {
    // Sync search mode from desktop chips
    const activeModeChip = document.querySelector(
      ".search-modes .mode-chip.active",
    );
    if (activeModeChip) {
      const mode = activeModeChip.textContent.trim().toLowerCase();
      if (mode === "files" || mode === "content") {
        currentSearchMode = mode;
      }
    }

    // Sync filter from desktop tabs
    const activeFilterTab = document.querySelector(
      ".filter-tabs .filter-tab.active",
    );
    if (activeFilterTab) {
      const text = activeFilterTab.textContent.trim().toLowerCase();
      if (text === "all") currentFilter = "all";
      else if (text === "in progress") currentFilter = "in_progress";
      else if (text === "completed") currentFilter = "completed";
      else if (text === "failed") currentFilter = "failed";
    }

    // Sync sort from desktop selects
    const sortBySelect = document.getElementById("sort-by");
    const sortOrderSelect = document.getElementById("sort-order");
    if (sortBySelect) currentSortBy = sortBySelect.value;
    if (sortOrderSelect) currentSortOrder = sortOrderSelect.value;

    // Update bottom sheet UI
    updateSheetUI();
    syncFiltersButtonBadge();
  }

  function updateSheetUI() {
    if (sheetSearchModes) {
      const chips = sheetSearchModes.querySelectorAll("[data-mode]");
      chips.forEach((c) =>
        c.classList.toggle(
          "bottom-sheet__chip--active",
          c.dataset.mode === currentSearchMode,
        ),
      );
    }

    if (sheetFilterOptions) {
      const options = sheetFilterOptions.querySelectorAll("[data-filter]");
      options.forEach((o) =>
        o.classList.toggle(
          "bottom-sheet__option--active",
          o.dataset.filter === currentFilter,
        ),
      );
    }

    if (sheetSortOptions) {
      const options = sheetSortOptions.querySelectorAll("[data-sort]");
      options.forEach((o) =>
        o.classList.toggle(
          "bottom-sheet__option--active",
          o.dataset.sort === currentSortBy,
        ),
      );
    }

    if (sheetSortOrder) {
      const chips = sheetSortOrder.querySelectorAll("[data-order]");
      chips.forEach((c) =>
        c.classList.toggle(
          "bottom-sheet__chip--active",
          c.dataset.order === currentSortOrder,
        ),
      );
    }
  }

  function destroy() {
    if (!isInitialized) return;

    if (filtersBtn) {
      filtersBtn.removeEventListener("click", handleFiltersClick);
    }

    if (sheetSearchModes) {
      sheetSearchModes.removeEventListener("click", handleSearchModeClick);
    }

    if (sheetFilterOptions) {
      sheetFilterOptions.removeEventListener("click", handleFilterClick);
    }

    if (sheetSortOptions) {
      sheetSortOptions.removeEventListener("click", handleSortClick);
    }

    if (sheetSortOrder) {
      sheetSortOrder.removeEventListener("click", handleSortOrderClick);
    }

    if (bottomSheet) {
      bottomSheet.destroy();
      bottomSheet = null;
    }

    isInitialized = false;
  }

  return {
    init,
    destroy,
    syncFromDesktop,
  };
}
